import React from 'react'

import { fmtTs, timeAgo } from '@/utils/format'

/**
 * Single, consistent freshness signal for detail hero areas.
 *
 * Renders a small pill with the relative time ("3h ago") and an absolute
 * tooltip with the locale timestamp. Optionally pass a `staleAfterMs`
 * threshold to switch the pill into a "stale" visual variant once data
 * crosses the limit.
 *
 * Usage:
 * ```tsx
 * <Freshness label="Memo generated" iso={memo_generated_at} />
 * <Freshness label="Last refreshed" iso={updated_at} staleAfterMs={24 * 3600 * 1000} />
 * ```
 */
export type FreshnessProps = {
  iso?: string | null
  label: string
  staleAfterMs?: number
}

export default function Freshness({ iso, label, staleAfterMs }: FreshnessProps) {
  if (!iso) {
    return (
      <span className="pill" title={`${label}: unknown`} style={{ fontSize: 11 }}>
        {label}: —
      </span>
    )
  }
  const d = new Date(String(iso))
  const t = d.getTime()
  if (!Number.isFinite(t)) {
    return (
      <span className="pill" title={`${label}: ${String(iso)}`} style={{ fontSize: 11 }}>
        {label}: —
      </span>
    )
  }
  const ageMs = Date.now() - t
  const isStale =
    typeof staleAfterMs === 'number' && staleAfterMs > 0 ? ageMs > staleAfterMs : false
  const cls = isStale ? 'pill warn' : 'pill'
  return (
    <span className={cls} title={`${label}: ${fmtTs(iso)}`} style={{ fontSize: 11 }}>
      {label}: {timeAgo(iso)}
    </span>
  )
}
